import type { ColumnDef } from "@tanstack/react-table";

import { Button, Combobox, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, FormCheckbox, IconInfo, Table, TableActionsDropdown, Text } from "@sentricmusic/ui";
// Value imports
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetArtistISRCS, useGetMySongsDetailed, useGetMyWriters } from "@/api/queries";
import LinkToExistingSongModal from "@/features/songs/album/components/LinkToExistingSongModal";
import useStringSimilarity from "@/hooks/useStringSimilarity";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import type { SelectedTrack } from "../store/multiSongStore";
import type { SimilarityResult } from "../types/similarityTypes";

import { getTitleThreshold, SIMILARITY_THRESHOLDS } from "../constants/similarityThresholds";
import useMultiSongStore from "../store/multiSongStore";
import { validateSimilarityMatch } from "../utils/similarityValidation";
import { areTracksLikelyVariants, determineOriginalVsVariant, isTrackVariant, stripVariantSuffix } from "../utils/trackVariantDetection";

export default function SelectSongsStep() {
  const { t } = useTranslation("album");
  const selectedAccount = useAppStore(state => state.selectedAccount) as string;
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const {
    tracks,
    isLoading,
    sourceType,
    toggleTrackSelection,
    selectAllTracks,
    toggleTrackRemix,
    toggleTrackContainsSamples,
    setAllTracksRemix,
    setAllTracksContainsSamples,
    setUserISRCs,
    markTrackAsSuspectedDuplicate,
    markTrackAsSuspectedIntraAlbumDuplicate,
    reclassifyVariantAsAccountMatch,
    updateTrackTargetWork,
    updateTrackTargetAlbumTrack,
    setTrackReviewAction,
    attachAuxiliaryWriters,
  } = useMultiSongStore();

  // Dialog state for suspected duplicate handling
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTrackForDialog, setSelectedTrackForDialog] = useState<SelectedTrack | null>(null);
  const [selectedWorkForDialog, setSelectedWorkForDialog] = useState<string>("");
  const [selectedAuxiliaryWriters, setSelectedAuxiliaryWriters] = useState<string[]>([]);
  // Which category the dialog was opened from — scopes Previous/Next navigation
  const dialogCategoryRef = useRef<"account" | "intra-album" | null>(null);
  // Snapshot of track IDs when dialog opens — keeps counter stable as tracks are reviewed/dismissed
  const dialogTrackIdsRef = useRef<string[]>([]);

  // Manual link to existing song modal state
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [selectedTrackForLink, setSelectedTrackForLink] = useState<SelectedTrack | null>(null);

  // Split suspected tracks into two distinct categories
  const accountMatchTracks = useMemo(() => {
    return tracks.filter(t =>
      t.isSuspectedDuplicate
      && !t.isDuplicate
      && t.isSelected
      && typeof t.suspectedWorkCode === "string" && t.suspectedWorkCode.length > 0
      && !(typeof t.suspectedAlbumTrackId === "string" && t.suspectedAlbumTrackId.length > 0),
    );
  }, [tracks]);

  const intraAlbumTracks = useMemo(() => {
    return tracks.filter(t =>
      t.isSuspectedDuplicate
      && !t.isDuplicate
      && t.isSelected
      && typeof t.suspectedAlbumTrackId === "string" && t.suspectedAlbumTrackId.length > 0,
    );
  }, [tracks]);

  // Current index within the snapshot (stable — doesn't shrink as tracks are dismissed)
  const currentSuspectedIndex = useMemo(() => {
    if (!selectedTrackForDialog)
      return -1;
    return dialogTrackIdsRef.current.indexOf(selectedTrackForDialog.id);
  }, [selectedTrackForDialog]);

  // Total count from the snapshot
  const totalSuspectedCount = dialogTrackIdsRef.current.length;

  // Check if there are any suspected tracks before the current one (for Previous button)
  const hasPreviousSuspected = useMemo(() => {
    return currentSuspectedIndex > 0;
  }, [currentSuspectedIndex]);

  // Fetch user's existing ISRCs for deduplication
  const isrcQuery = useGetArtistISRCS({ aac: selectedAccount });
  const isrcData = isrcQuery.data;

  // Fetch user's existing songs for title similarity detection
  const songsQuery = useGetMySongsDetailed({
    aac: selectedAccount,
    includePerformers: true, // Include performing artists for better matching
  });
  const userSongs = useMemo(() => {
    const works = songsQuery.data?.works;
    return Array.isArray(works) ? works : [];
  }, [songsQuery.data?.works]);
  const songsLoading = Boolean(songsQuery.isLoading);

  // Fetch user's writers for auxiliary writer selection
  const writersQuery = useGetMyWriters({ aac: selectedAccount });
  const userWriters = useMemo(() => {
    const writers = writersQuery.data?.writers;
    return Array.isArray(writers) ? writers : [];
  }, [writersQuery.data?.writers]);

  // String similarity hook (uses Jaro-Winkler algorithm by default)
  const { compareTwoStrings } = useStringSimilarity();

  // Track which tracks have already been marked to prevent infinite loops
  const markedTracksRef = useRef<Set<string>>(new Set());

  // Stable identity key that only changes when track identity changes
  // Excludes reviewAction — review decisions don't alter which tracks exist or their names,
  // and the similarity body already skips reviewed tracks via early return.
  // This prevents O(N×M) similarity recomputation on every modal action (dismiss/attach/remove).
  const trackIdentityKey = useMemo(
    () => tracks.map(t => `${t.id}|${t.name}|${t.isDuplicate}`).join("~"),
    [tracks],
  );

  // Calculate similarity data once and memoize it
  // Uses trackIdentityKey instead of tracks to avoid recomputing on every checkbox toggle
  const similarityData = useMemo((): Map<string, SimilarityResult> => {
    const currentTracks = useMultiSongStore.getState().tracks;
    if (songsLoading || userSongs.length === 0 || currentTracks.length === 0) {
      return new Map();
    }

    const results = new Map<string, SimilarityResult>();

    currentTracks.forEach((track) => {
      if (track.isDuplicate)
        return;

      // Skip tracks that have already been reviewed by the user
      // This preserves user decisions (dismiss/attach-recording) when navigating back
      if (track.reviewAction != null) {
        return;
      }

      let bestMatch = null;
      let hasArtistMatch = false;
      let titleSimilarity = 0;

      for (const userSong of userSongs) {
        if (!userSong?.title || typeof userSong.title !== "string")
          continue;

        // Skip very short titles (likely data quality issues or single letters)
        if (userSong.title.trim().length <= 2) {
          continue;
        }

        // Normalize both titles by removing variant suffixes (Live, Acoustic, Remix, etc.) before comparison
        const normalizedTrackTitle = stripVariantSuffix(track.name);
        const normalizedUserSongTitle = stripVariantSuffix(userSong.title);

        const currentTitleSimilarity = compareTwoStrings(normalizedTrackTitle, normalizedUserSongTitle);

        // Check if artists match
        let currentArtistMatch = false;
        if (Array.isArray(userSong.performingartists) && userSong.performingartists.length > 0) {
          for (const trackArtist of track.artists) {
            for (const userArtist of userSong.performingartists) {
              if (
                userArtist?.performingartistname
                && typeof userArtist.performingartistname === "string"
                && !userArtist.isdeleted
              ) {
                const artistSimilarity = compareTwoStrings(trackArtist.name, userArtist.performingartistname);
                if (artistSimilarity > SIMILARITY_THRESHOLDS.ARTIST_MATCH) {
                  currentArtistMatch = true;
                  break;
                }
              }
            }
            if (currentArtistMatch)
              break;
          }
        }

        // Determine if this is the best match so far
        const threshold = getTitleThreshold(currentArtistMatch);

        if (currentTitleSimilarity > threshold && currentTitleSimilarity > titleSimilarity) {
          // Validate the match to filter out false positives
          const validation = validateSimilarityMatch(track.name, userSong.title, currentTitleSimilarity);

          if (validation.isValid) {
            bestMatch = userSong;
            hasArtistMatch = currentArtistMatch;
            titleSimilarity = currentTitleSimilarity;
          }
        }
      }

      if (bestMatch) {
        results.set(track.id, {
          similarSong: bestMatch,
          titleSimilarity,
          hasArtistMatch,
          shouldShowWarning: true, // Already filtered by threshold above
        });
      }
    });

    return results;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songsLoading, userSongs, trackIdentityKey, compareTwoStrings]);

  // Mark tracks as suspected duplicates when similarity data changes
  // Use ref to prevent infinite loops by tracking which tracks we've already processed
  useEffect(() => {
    // Get fresh tracks from the store to ensure we have the latest reviewAction state
    const currentTracks = useMultiSongStore.getState().tracks;

    similarityData.forEach((similarity, trackId) => {
      // Skip if already marked in this session
      if (markedTracksRef.current.has(trackId)) {
        return;
      }

      // Skip if user has already reviewed this track (dismiss or attach-recording)
      // This preserves user decisions when navigating back to this step
      const track = currentTracks.find(t => t.id === trackId);
      if (track?.reviewAction != null) {
        markedTracksRef.current.add(trackId); // Add to ref so we don't check again
        return;
      }

      if (similarity.shouldShowWarning === true && similarity.similarSong !== null && similarity.similarSong !== undefined) {
        const song = similarity.similarSong as { workcode?: string; title?: string };
        if (typeof song.workcode === "string" && song.workcode.length > 0 && typeof song.title === "string" && song.title.length > 0) {
          markTrackAsSuspectedDuplicate(trackId, song.workcode, song.title);
          markedTracksRef.current.add(trackId);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [similarityData]); // Intentionally omitting markTrackAsSuspectedDuplicate to prevent infinite loop

  // Intra-album similarity detection - detect variants within the same album
  // IMPORTANT: This must check similarityData to ensure account-level matches take priority
  // Uses trackIdentityKey instead of tracks to avoid recomputing on every checkbox toggle
  const intraAlbumMatches = useMemo(() => {
    const currentTracks = useMultiSongStore.getState().tracks;
    if (currentTracks.length === 0)
      return new Map<string, { matchedTrackId: string; matchedTrackTitle: string; isOriginal: boolean }>();

    const matches = new Map<string, { matchedTrackId: string; matchedTrackTitle: string; isOriginal: boolean }>();

    // Helper to check if a track has an account-level match (from similarityData)
    const hasAccountLevelMatch = (trackId: string): boolean => {
      const similarity = similarityData.get(trackId);
      return similarity?.shouldShowWarning === true && similarity?.similarSong != null;
    };

    // Compare each track against every other track in the album
    for (let i = 0; i < currentTracks.length; i++) {
      const track1 = currentTracks[i];
      // Skip tracks that have been reviewed by user (dismiss/attach-recording)
      if (track1.reviewAction != null)
        continue;
      // Skip ISRC duplicates and tracks that already match an existing account work
      // Rely solely on similarityData (synchronous, same render) — NOT on track.isSuspectedDuplicate
      // which is set asynchronously via useEffect and may lag behind, causing a race condition
      // where variants get incorrectly classified as account-level title matches.
      if (track1.isDuplicate || hasAccountLevelMatch(track1.id))
        continue;

      for (let j = i + 1; j < currentTracks.length; j++) {
        const track2 = currentTracks[j];
        // Skip tracks that have been reviewed by user (dismiss/attach-recording)
        if (track2.reviewAction != null)
          continue;
        // Skip ISRC duplicates and tracks that already match an existing account work
        // Same rationale as above — use similarityData, not the store flag
        if (track2.isDuplicate || hasAccountLevelMatch(track2.id))
          continue;

        // Method 1: Check if they're likely variants using pattern detection
        if (areTracksLikelyVariants(track1.name, track2.name)) {
          // Determine which is original and which is variant
          const determination = determineOriginalVsVariant(
            { id: track1.id, name: track1.name, track_number: track1.track_number },
            { id: track2.id, name: track2.name, track_number: track2.track_number },
          );

          if (determination != null) {
            // Mark the variant track
            if (determination.variantId === track1.id) {
              matches.set(track1.id, {
                matchedTrackId: track2.id,
                matchedTrackTitle: track2.name,
                isOriginal: false,
              });
            }
            else {
              matches.set(track2.id, {
                matchedTrackId: track1.id,
                matchedTrackTitle: track1.name,
                isOriginal: false,
              });
            }
            continue; // Found a match, move to next comparison
          }
        }

        // Method 2: Use string similarity on stripped titles
        // Only applies when at least one title has a variant pattern (Live, Remix, Acoustic, etc.)
        // Without this guard, Jaro-Winkler's prefix bonus causes false positives for
        // short titles sharing common prefixes (e.g. "The Florist" vs "The Word is Out" = 0.88)
        const track1HasVariant = isTrackVariant(track1.name);
        const track2HasVariant = isTrackVariant(track2.name);

        if (track1HasVariant || track2HasVariant) {
          const strippedTitle1 = stripVariantSuffix(track1.name).toLowerCase();
          const strippedTitle2 = stripVariantSuffix(track2.name).toLowerCase();

          if (strippedTitle1.length > 2 && strippedTitle2.length > 2) {
            const similarity = compareTwoStrings(strippedTitle1, strippedTitle2);

            // High similarity threshold for intra-album matches
            if (similarity >= SIMILARITY_THRESHOLDS.INTRA_ALBUM_VARIANT) {
              // Determine which is original using heuristics
              const determination = determineOriginalVsVariant(
                { id: track1.id, name: track1.name, track_number: track1.track_number },
                { id: track2.id, name: track2.name, track_number: track2.track_number },
              );

              if (determination != null) {
                // Mark the variant track
                if (determination.variantId === track1.id) {
                  matches.set(track1.id, {
                    matchedTrackId: track2.id,
                    matchedTrackTitle: track2.name,
                    isOriginal: false,
                  });
                }
                else {
                  matches.set(track2.id, {
                    matchedTrackId: track1.id,
                    matchedTrackTitle: track1.name,
                    isOriginal: false,
                  });
                }
              }
            }
          }
        }
      }
    }

    return matches;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackIdentityKey, compareTwoStrings, similarityData]);

  // Mark tracks with intra-album matches
  useEffect(() => {
    // Get fresh tracks from the store to ensure we have the latest reviewAction state
    const currentTracks = useMultiSongStore.getState().tracks;

    intraAlbumMatches.forEach((match, trackId) => {
      // Skip if already marked (don't override account-level matches)
      if (markedTracksRef.current.has(trackId)) {
        return;
      }

      // Skip if user has already reviewed this track (dismiss or attach-recording)
      // This preserves user decisions when navigating back to this step
      const track = currentTracks.find(t => t.id === trackId);
      if (track?.reviewAction != null) {
        markedTracksRef.current.add(trackId); // Add to ref so we don't check again
        return;
      }

      markTrackAsSuspectedIntraAlbumDuplicate(trackId, match.matchedTrackId, match.matchedTrackTitle, match.isOriginal);
      markedTracksRef.current.add(trackId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intraAlbumMatches]); // Intentionally omitting markTrackAsSuspectedIntraAlbumDuplicate to prevent infinite loop

  // Import loading — blocks all table interaction while Spotify data is being fetched
  const isImportLoading = Boolean(isLoading);

  // Type guard to safely check if data has the expected structure
  const isValidISRCData = (data: unknown): data is { isrc_list: Array<{ isrc: string; workcode: string }> } => {
    return (
      typeof data === "object"
      && data !== null
      && "isrc_list" in data
      && Array.isArray((data as { isrc_list: unknown }).isrc_list)
    );
  };

  // Set user ISRCs in the store when they load
  // Also clear markedTracksRef for tracks that had their variant status cleared
  // so account-level matching can re-run for them
  useEffect(() => {
    if (isValidISRCData(isrcData)) {
      const isrcs = isrcData.isrc_list
        .filter((item): item is { isrc: string; workcode: string } =>
          typeof item === "object"
          && item !== null
          && "isrc" in item
          && typeof item.isrc === "string",
        )
        .map(item => item.isrc);
      const isrcSet = new Set(isrcs);

      // Find tracks that will have their variant status cleared
      // (tracks whose suspectedAlbumTrackId target is becoming an ISRC duplicate)
      // Use current store state to avoid dependency on tracks
      const currentTracks = useMultiSongStore.getState().tracks;

      currentTracks.forEach((track) => {
        if (track.suspectedAlbumTrackId != null) {
          const targetTrack = currentTracks.find(t => t.id === track.suspectedAlbumTrackId);
          const targetIsrc = targetTrack?.external_ids?.isrc;
          if (targetIsrc != null && targetIsrc !== "" && isrcSet.has(targetIsrc)) {
            // This track's variant target is becoming a duplicate
            // Remove from markedTracksRef so account-level match can re-run
            markedTracksRef.current.delete(track.id);
          }
        }
      });

      setUserISRCs(isrcs);
    }
  }, [isrcData, setUserISRCs]);

  // Simplified function since we no longer need to track manual interactions
  const handleManualToggleTrackSelection = useCallback((trackId: string) => {
    toggleTrackSelection(trackId);
  }, [toggleTrackSelection]);

  // Cascade: when a track is confirmed as a recording of an account work,
  // reclassify any intra-album variants that point at it as account-level matches
  // against the same account work. This handles the "trifold" scenario:
  //   1. Account has "LITTLE BRITAIN" (existing work)
  //   2. Playlist has "Little Britain" → title match → user confirms as recording of "LITTLE BRITAIN"
  //   3. Playlist has "Little Britain - Remastered 2012" → variant of "Little Britain" (step 2)
  //   → After step 2 is confirmed, step 3 should now show as a title match against "LITTLE BRITAIN"
  const cascadeVariantsToAccountMatch = useCallback((confirmedTrackId: string, workCode: string, workTitle: string) => {
    const currentTracks = useMultiSongStore.getState().tracks;
    currentTracks.forEach((track) => {
      if (track.suspectedAlbumTrackId === confirmedTrackId) {
        // Reclassify this variant as an account-level match against the same work
        reclassifyVariantAsAccountMatch(track.id, workCode, workTitle);
        // Remove from markedTracksRef so the account-level marking useEffect
        // won't skip it (it checks markedTracksRef before processing)
        markedTracksRef.current.delete(track.id);
      }
    });
  }, [reclassifyVariantAsAccountMatch]);

  // Format track duration from milliseconds to MM:SS
  const formatDuration = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Transform works into Combobox options for dialog
  const workOptions = useMemo(() => {
    const works = songsQuery.data?.works;
    if (!Array.isArray(works)) {
      return [];
    }
    return works.map((work) => {
      const performingArtists = work.performingartists
        ?.filter(pa => pa != null && !pa.isdeleted && typeof pa.performingartistname === "string" && pa.performingartistname.length > 0)
        .map(pa => pa.performingartistname)
        .join(", ");

      const label = performingArtists
        ? `${work.title || "Untitled"} - ${performingArtists}`
        : work.title || "Untitled";

      return {
        label,
        value: work.workcode || "",
      };
    });
  }, [songsQuery.data?.works]);

  // Create album track options for the link modal (only original tracks, not variants/recordings)
  // Exclude the currently selected track from the options
  const albumTrackOptions = useMemo(() => {
    if (!selectedTrackForLink)
      return [];

    return tracks
      .filter((t) => {
        // Exclude the track we're trying to link
        if (t.id === selectedTrackForLink.id)
          return false;
        // Exclude duplicates (already in account)
        if (t.isDuplicate)
          return false;
        // Exclude variants (tracks that are already linked to another track in this album)
        if (t.isOriginalInAlbum === false)
          return false;
        // Exclude tracks that are already set to be attached as recordings to an account work
        if (t.reviewAction === "attach-recording" && typeof t.suspectedWorkCode === "string" && t.suspectedWorkCode.length > 0)
          return false;
        // Only include selected tracks
        if (!t.isSelected)
          return false;
        return true;
      })
      .map((t) => {
        const artistName = t.artists?.map(a => a.name).join(", ");
        const label = artistName ? `${t.name} - ${artistName}` : t.name;
        return {
          label,
          value: t.id,
        };
      });
  }, [tracks, selectedTrackForLink]);

  // Dialog handlers
  const handleOpenDialog = useCallback((track: SelectedTrack, category?: "account" | "intra-album") => {
    setSelectedTrackForDialog(track);
    setSelectedWorkForDialog(track.suspectedWorkCode ?? "");
    setSelectedAuxiliaryWriters(track.auxiliaryWriters ?? []);
    // Infer category from track if not explicitly provided
    const inferredCategory = category ?? (
      typeof track.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0
        ? "intra-album"
        : "account"
    );
    dialogCategoryRef.current = inferredCategory;
    // Snapshot the track IDs for stable counter display
    const list = inferredCategory === "account"
      ? accountMatchTracks
      : inferredCategory === "intra-album"
        ? intraAlbumTracks
        : [...accountMatchTracks, ...intraAlbumTracks];
    dialogTrackIdsRef.current = list.map(t => t.id);
    setDialogOpen(true);
  }, [accountMatchTracks, intraAlbumTracks]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedTrackForDialog(null);
    setSelectedWorkForDialog("");
    setSelectedAuxiliaryWriters([]);
    dialogCategoryRef.current = null;
    dialogTrackIdsRef.current = [];
  }, []);

  const handleNextUnreviewed = useCallback(() => {
    if (selectedTrackForDialog) {
      const snapshotIds = dialogTrackIdsRef.current;
      const currentIndex = snapshotIds.indexOf(selectedTrackForDialog.id);

      // Look for the next unreviewed track in snapshot order
      const currentTracks = useMultiSongStore.getState().tracks;
      let nextTrack = null;
      for (let i = currentIndex + 1; i < snapshotIds.length; i++) {
        const track = currentTracks.find(t => t.id === snapshotIds[i]);
        if (track && !track.reviewAction) {
          nextTrack = track;
          break;
        }
      }

      if (nextTrack) {
        setSelectedTrackForDialog(nextTrack);
        setSelectedWorkForDialog(nextTrack.suspectedWorkCode ?? "");
        setSelectedAuxiliaryWriters(nextTrack.auxiliaryWriters ?? []);
      }
      else {
        // No more unreviewed tracks in this category - close dialog
        handleCloseDialog();
      }
    }
    else {
      handleCloseDialog();
    }
  }, [selectedTrackForDialog, handleCloseDialog]);

  const handlePreviousSuspected = useCallback(() => {
    if (selectedTrackForDialog) {
      const snapshotIds = dialogTrackIdsRef.current;
      const currentIndex = snapshotIds.indexOf(selectedTrackForDialog.id);
      if (currentIndex > 0) {
        // Navigate to the previous track in snapshot order
        const currentTracks = useMultiSongStore.getState().tracks;
        const prevTrack = currentTracks.find(t => t.id === snapshotIds[currentIndex - 1]);
        if (prevTrack) {
          setSelectedTrackForDialog(prevTrack);
          setSelectedWorkForDialog(prevTrack.suspectedWorkCode ?? "");
          setSelectedAuxiliaryWriters(prevTrack.auxiliaryWriters ?? []);
        }
      }
    }
  }, [selectedTrackForDialog]);

  const handleConfirmAsRecording = useCallback(() => {
    if (!selectedTrackForDialog)
      return;

    // Check if this is an intra-album match
    const isIntraAlbumMatch = typeof selectedTrackForDialog.suspectedAlbumTrackId === "string" && selectedTrackForDialog.suspectedAlbumTrackId.length > 0;

    if (isIntraAlbumMatch) {
      // For intra-album matches, mark as explicitly confirmed
      setTrackReviewAction(selectedTrackForDialog.id, "attach-recording");
    }
    else if (selectedWorkForDialog) {
      // For account matches, mark as explicitly confirmed and update target work if changed
      setTrackReviewAction(selectedTrackForDialog.id, "attach-recording");
      // Resolve the final work code and title for cascading
      let finalWorkCode = selectedWorkForDialog;
      let finalWorkTitle = selectedTrackForDialog.suspectedWorkTitle ?? "";
      if (selectedWorkForDialog !== selectedTrackForDialog.suspectedWorkCode) {
        // User changed the target work
        const work = userSongs.find(s => s.workcode === selectedWorkForDialog);
        if (work && typeof work.title === "string") {
          updateTrackTargetWork(selectedTrackForDialog.id, selectedWorkForDialog, work.title);
          finalWorkCode = selectedWorkForDialog;
          finalWorkTitle = work.title;
        }
      }
      // Cascade: reclassify any intra-album variants pointing at this track
      // so they become account-level matches against the same work
      cascadeVariantsToAccountMatch(selectedTrackForDialog.id, finalWorkCode, finalWorkTitle);
    }
    // Navigate to next unreviewed track instead of closing
    handleNextUnreviewed();
  }, [selectedTrackForDialog, selectedWorkForDialog, setTrackReviewAction, updateTrackTargetWork, userSongs, handleNextUnreviewed, cascadeVariantsToAccountMatch]);

  const handleMarkAsNewWork = useCallback(() => {
    if (selectedTrackForDialog) {
      setTrackReviewAction(selectedTrackForDialog.id, "dismiss");
      // Attach auxiliary writers if any were selected
      if (selectedAuxiliaryWriters.length > 0) {
        attachAuxiliaryWriters(selectedTrackForDialog.id, selectedAuxiliaryWriters);
      }
    }
    // Navigate to next unreviewed track instead of closing
    handleNextUnreviewed();
  }, [selectedTrackForDialog, selectedAuxiliaryWriters, setTrackReviewAction, attachAuxiliaryWriters, handleNextUnreviewed]);

  // Handle dismissing a false positive match (e.g., "Deep Fake" matching "Deep")
  // Mark as reviewed with "dismiss" action so the track stays in the progress card
  // counts and the card shows "Complete" instead of disappearing.
  const handleDismissFalsePositive = useCallback(() => {
    if (selectedTrackForDialog) {
      setTrackReviewAction(selectedTrackForDialog.id, "dismiss");
    }
    // Navigate to next unreviewed track instead of closing
    handleNextUnreviewed();
  }, [selectedTrackForDialog, setTrackReviewAction, handleNextUnreviewed]);

  // Handle opening the link to existing song modal
  const handleOpenLinkModal = useCallback((track: SelectedTrack) => {
    setSelectedTrackForLink(track);
    setLinkModalOpen(true);
  }, []);

  // Handle confirming the link to an existing account song
  const handleConfirmAccountLink = useCallback((trackId: string, workCode: string, workTitle: string) => {
    // Mark the track as suspected duplicate and set the target work
    markTrackAsSuspectedDuplicate(trackId, workCode, workTitle);
    // Set the review action to attach-recording
    setTrackReviewAction(trackId, "attach-recording");
    // Update the target work
    updateTrackTargetWork(trackId, workCode, workTitle);
    // Cascade: reclassify any intra-album variants pointing at this track
    // so they become account-level matches against the same work
    cascadeVariantsToAccountMatch(trackId, workCode, workTitle);
  }, [markTrackAsSuspectedDuplicate, setTrackReviewAction, updateTrackTargetWork, cascadeVariantsToAccountMatch]);

  // Handle confirming the link to an album track (intra-album variant)
  const handleConfirmAlbumLink = useCallback((trackId: string, targetTrackId: string, targetTrackTitle: string) => {
    // Mark the track as a variant of another album track (not the original)
    markTrackAsSuspectedIntraAlbumDuplicate(trackId, targetTrackId, targetTrackTitle, false);
    // Set the review action to attach-recording
    setTrackReviewAction(trackId, "attach-recording");
    // Update the target album track
    updateTrackTargetAlbumTrack(trackId, targetTrackId, targetTrackTitle);
  }, [markTrackAsSuspectedIntraAlbumDuplicate, setTrackReviewAction, updateTrackTargetAlbumTrack]);

  // Define table columns
  const columns = useMemo<ColumnDef<SelectedTrack>[]>(() => [
    {
      id: "selection",
      header: ({ table }) => (
        <FormCheckbox
          defaultChecked={false}
          checked={
            !isImportLoading
            && table.getRowModel().rows.length > 0
            && table.getRowModel().rows.filter(row => !row.original.isDuplicate).every(row => Boolean(row.original.isSelected))
          }
          onChange={(e) => {
            // Only select/deselect non-duplicate tracks when not loading
            if (!isImportLoading) {
              const currentTracks = useMultiSongStore.getState().tracks;
              const nonDuplicateTracks = currentTracks.filter(t => !t.isDuplicate);
              if (nonDuplicateTracks.length > 0) {
                selectAllTracks(Boolean(e.target.checked));
              }
            }
          }}
          disabled={isImportLoading}
          tableHeader={true}
        />
      ),
      cell: ({ row }) => {
        const isDuplicate = row.original.isDuplicate;

        // Don't render checkbox for duplicate tracks
        if (isDuplicate) {
          return <div className="w-4 h-4 p-2 -m-2" />; // Empty placeholder to maintain layout
        }

        // Handle click on checkbox
        const handleCheckboxClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          if (!isImportLoading) {
            handleManualToggleTrackSelection(row.original.id);
          }
        };

        const isLoadingDisabled = isImportLoading;

        return (
          <div
            className={`p-2 -m-2 ${isLoadingDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
            onClick={() => {
              if (!isLoadingDisabled) {
                handleManualToggleTrackSelection(row.original.id);
              }
            }}
          >
            <div onClick={handleCheckboxClick}>
              <FormCheckbox
                defaultChecked={false}
                checked={row.original.isSelected}
                onChange={() => {}} // Empty onChange handler, actual toggle happens in onClick
                className="w-4 h-4"
                disabled={isLoadingDisabled}
              />
            </div>
          </div>
        );
      },
      size: 5,
    },
    // {
    //   id: "track_number",
    //   header: "#",
    //   accessorFn: row => row.track_number,
    //   cell: ({ row }) => (
    //     <Text as="p" className="font-normal">
    //       {row.original.track_number}
    //     </Text>
    //   ),
    //   size: 5,
    // },
    {
      id: "title",
      header: t("album:title") ?? "Title",
      accessorFn: row => row.name,
      cell: ({ row }) => {
        const isDuplicate = row.original.isDuplicate;
        const isSuspectedDuplicate = row.original.isSuspectedDuplicate;
        const isMarkedAsNewWork = row.original.reviewAction === "dismiss";
        const isrc = row.original.external_ids?.isrc;
        const isDisabled = isImportLoading || isDuplicate;

        return (
          <div className="flex flex-col">
            <Text
              as="p"
              className={`font-semibold ${
                isDuplicate
                  ? "text-gray-400 line-through cursor-not-allowed"
                  : isImportLoading
                    ? "cursor-not-allowed"
                    : "cursor-pointer"
              }`}
              onClick={() => {
                if (!isDisabled) {
                  handleManualToggleTrackSelection(row.original.id);
                }
              }}
            >
              {row.original.name}
            </Text>
            {Boolean(isDuplicate) && (
              <Text as="p" className="text-xs text-ink-error">
                {t("album:already-in-account") ?? "Already in your account"}
                {isrc !== null && isrc !== undefined && isrc !== "" ? ` (ISRC: ${isrc})` : ""}
              </Text>
            )}
            {Boolean(isSuspectedDuplicate && !isDuplicate && row.original.reviewAction !== "dismiss") && (() => {
              // Determine if this is an account match or intra-album match
              const isIntraAlbumMatch = typeof row.original.suspectedAlbumTrackId === "string" && row.original.suspectedAlbumTrackId.length > 0;
              const matchTitle = isIntraAlbumMatch ? row.original.suspectedAlbumTrackTitle : row.original.suspectedWorkTitle;

              // For account matches, get performing artists from the matched work
              let performingArtists = "";
              if (!isIntraAlbumMatch && typeof row.original.suspectedWorkCode === "string" && row.original.suspectedWorkCode.length > 0) {
                const matchedWork = userSongs.find(s => s.workcode === row.original.suspectedWorkCode);
                if (matchedWork?.performingartists && Array.isArray(matchedWork.performingartists)) {
                  const artistNames = matchedWork.performingartists
                    .filter(a => a != null && typeof a.performingartistname === "string" && !a.isdeleted)
                    .map(a => a.performingartistname)
                    .slice(0, 2); // Show max 2 artists
                  if (artistNames.length > 0) {
                    performingArtists = ` (${artistNames.join(", ")})`;
                  }
                }
              }

              // Check if the track has been reviewed (either marked as new work OR explicitly confirmed as recording)
              const isConfirmedAsRecording = row.original.reviewAction === "attach-recording";
              const hasBeenReviewed = isMarkedAsNewWork || isConfirmedAsRecording;

              let labelText = "";
              if (isMarkedAsNewWork) {
                if (isIntraAlbumMatch) {
                  labelText = sourceType === "playlist"
                    ? t("album:review-playlist-variant-confirmed-separate", { defaultValue: "Review - Playlist variant of \"{{title}}\" - Confirmed as separate song", title: matchTitle })
                    : t("album:review-album-variant-confirmed-separate", { defaultValue: "Review - Album variant of \"{{title}}\" - Confirmed as separate song", title: matchTitle });
                }
                else {
                  labelText = t("album:confirmed-as-new-song", "Confirmed as new song");
                }
              }
              else if (isConfirmedAsRecording) {
                if (isIntraAlbumMatch) {
                  labelText = t("album:confirmed-as-recording-of", { defaultValue: "Confirmed as recording of \"{{title}}\"", title: matchTitle });
                }
                else {
                  labelText = t("album:confirmed-as-recording-of-with-artists", { defaultValue: "Confirmed as recording of \"{{title}}\"{{artists}}", title: matchTitle, artists: performingArtists });
                }
              }
              else {
                if (isIntraAlbumMatch) {
                  labelText = sourceType === "playlist"
                    ? t("album:review-playlist-variant-of", { defaultValue: "Review - Playlist variant of \"{{title}}\"", title: matchTitle })
                    : t("album:review-album-variant-of", { defaultValue: "Review - Album variant of \"{{title}}\"", title: matchTitle });
                }
                else {
                  labelText = t("album:title-match-with-artists", { defaultValue: "Title match - \"{{title}}\"{{artists}}", title: matchTitle, artists: performingArtists });
                }
              }

              return (
                <Button
                  variant={isTunecore ? "secondary" : "primary"}
                  label={labelText}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDialog(row.original);
                  }}
                  className={`justify-start mt-1 text-xs font-semibold text-left normal-case whitespace-normal w-max ${
                    !isTunecore
                      ? (hasBeenReviewed ? "bg-green-500 hover:bg-green-600 active:bg-green-600" : "bg-action-secondary hover:bg-action-secondary-hover active:bg-action-secondary-pressed")
                      : ""
                  }`}
                />
              );
            })()}
          </div>
        );
      },
      minSize: 500,
    },
    {
      id: "artist",
      header: t("album:artist") ?? "Artist",
      accessorFn: row => row.artists.map(artist => artist.name).join(", "),
      cell: ({ row }) => (
        <Text as="p" className="font-normal">
          {row.original.artists.map(artist => artist.name).join(", ")}
        </Text>
      ),

    },
    {
      id: "duration",
      header: t("album:duration") ?? "Duration",
      accessorFn: row => row.duration_ms,
      cell: ({ row }) => (
        <Text as="p" className="font-normal">
          {formatDuration(row.original.duration_ms)}
        </Text>
      ),

    },
    {
      id: "containsSamples",
      header: () => (
        <FormCheckbox
          label={t("album:contains-samples-question") ?? "Contains Samples?"}
          checked={(() => {
            const currentTracks = useMultiSongStore.getState().tracks;
            return !isImportLoading && currentTracks.length > 0 && currentTracks.filter(t => !t.isDuplicate).every(track => Boolean(track.containsSamples));
          })()}
          onChange={(e) => {
            if (!isImportLoading) {
              setAllTracksContainsSamples(Boolean(e.target.checked));
            }
          }}
          className="w-4 h-4"
          smallFont={true}
          tableHeader={true}
          disabled={isImportLoading}
        />
      ),
      cell: ({ row }) => {
        const isDuplicate = row.original.isDuplicate;

        // Don't render checkbox for duplicate tracks
        if (isDuplicate) {
          return <div className="w-4 h-4" />; // Empty placeholder to maintain layout
        }

        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          if (!isImportLoading) {
            toggleTrackContainsSamples(row.original.id);
          }
        };

        const isLoadingDisabled = isImportLoading;

        return (
          <div onClick={handleClick}>
            <FormCheckbox
              checked={row.original.containsSamples || false}
              onChange={() => {}} // Empty onChange handler, actual toggle happens in onClick
              className="w-4 h-4"
              disabled={isLoadingDisabled}
            />
          </div>
        );
      },
      size: 185,
      minSize: 185,
    },
    {
      id: "isRemix",
      header: () => (
        <FormCheckbox
          label={t("album:is-remix-question") ?? "Is Remix?"}
          checked={(() => {
            const currentTracks = useMultiSongStore.getState().tracks;
            return !isImportLoading && currentTracks.length > 0 && currentTracks.filter(t => !t.isDuplicate).every(track => Boolean(track.isRemix));
          })()}
          onChange={(e) => {
            if (!isImportLoading) {
              setAllTracksRemix(Boolean(e.target.checked));
            }
          }}
          className="w-4 h-4"
          smallFont={true}
          tableHeader={true}
          disabled={isImportLoading}
        />
      ),
      cell: ({ row }) => {
        const isDuplicate = row.original.isDuplicate;

        // Don't render checkbox for duplicate tracks
        if (isDuplicate) {
          return <div className="w-4 h-4" />; // Empty placeholder to maintain layout
        }

        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          if (!isImportLoading) {
            toggleTrackRemix(row.original.id);
          }
        };

        const isLoadingDisabled = isImportLoading;

        return (
          <div onClick={handleClick}>
            <FormCheckbox
              checked={row.original.isRemix || false}
              onChange={() => {}} // Empty onChange handler, actual toggle happens in onClick
              className="w-4 h-4"
              disabled={isLoadingDisabled}
            />
          </div>
        );
      },
      size: 110,
      minSize: 110,
    },
    {
      id: "actions",
      enablePinning: true,
      enableResizing: false,
      meta: { pinSide: "right" },
      header: "",
      cell: ({ row }) => {
        const isDuplicate = row.original.isDuplicate;
        const isSuspectedDuplicate = row.original.isSuspectedDuplicate;
        const isVariant = row.original.isOriginalInAlbum === false; // This is a variant track

        // Don't show actions for:
        // - Duplicate tracks (ISRC already exists in account)
        // - Suspected duplicates (they have the Review button in the title column)
        // - Variants (they inherit from their original track)
        if (isDuplicate || isSuspectedDuplicate || isVariant) {
          return null;
        }

        // For non-flagged tracks, allow manual linking to existing songs
        return (
          <TableActionsDropdown disabled={isImportLoading}>
            <Button
              variant="dropdown"
              label={t("album:link-to-existing-song") ?? "Link to Existing Song"}
              onClick={() => handleOpenLinkModal(row.original)}
            />
          </TableActionsDropdown>
        );
      },
      size: 60,
      minSize: 60,
      maxSize: 60,
    },

  ], [selectAllTracks, handleManualToggleTrackSelection, toggleTrackRemix, toggleTrackContainsSamples, setAllTracksRemix, setAllTracksContainsSamples, t, isImportLoading, handleOpenDialog, userSongs, handleOpenLinkModal, isTunecore, sourceType]);

  // Calculate stats for the table header
  const statsBlock = useMemo(() => {
    const duplicateCount = tracks.filter(t => Boolean(t.isDuplicate)).length;

    // Count tracks with suspected similar titles that need review
    const unreviewedCount = tracks.filter(t =>
      Boolean(t.isSuspectedDuplicate)
      && !t.isDuplicate
      && t.isSelected
      && t.reviewAction === null,
    ).length;

    // Count tracks that have been reviewed
    const reviewedCount = tracks.filter(t =>
      Boolean(t.isSuspectedDuplicate)
      && !t.isDuplicate
      && t.isSelected
      && t.reviewAction !== null,
    ).length;

    const stats = [
      { title: t("album:tracks") ?? "Tracks", value: tracks.length || 0, highlight: false },
      { title: t("album:selected") ?? "Selected", value: tracks.filter(t => Boolean(t.isSelected)).length || 0, highlight: true },
    ];

    // Add duplicate count if there are any duplicates
    if (duplicateCount > 0) {
      stats.push({
        title: t("album:duplicates") ?? "Duplicates",
        value: duplicateCount,
        highlight: false,
      });
    }

    // Add unreviewed count if there are any
    if (unreviewedCount > 0) {
      stats.push({
        title: t("album:need-review") ?? "Need Review",
        value: unreviewedCount,
        highlight: false,
      });
    }

    // Add reviewed count if there are any
    if (reviewedCount > 0) {
      stats.push({
        title: t("album:reviewed") ?? "Reviewed",
        value: reviewedCount,
        highlight: false,
      });
    }

    return stats;
  }, [tracks, t]);

  // Check if there are any suspected duplicates or recordings to show explainer
  const hasSuspectedDuplicates = useMemo(() => {
    return tracks.some(track => track.isSuspectedDuplicate === true);
  }, [tracks]);

  // Progress data for the two review cards
  const accountMatchProgress = useMemo(() => {
    const total = accountMatchTracks.length;
    const reviewed = accountMatchTracks.filter(t => t.reviewAction !== null).length;
    return { total, reviewed, isComplete: total > 0 && reviewed === total };
  }, [accountMatchTracks]);

  const intraAlbumProgress = useMemo(() => {
    const total = intraAlbumTracks.length;
    const reviewed = intraAlbumTracks.filter(t => t.reviewAction !== null).length;
    return { total, reviewed, isComplete: total > 0 && reviewed === total };
  }, [intraAlbumTracks]);

  // Open the review modal scoped to a specific category, starting at the first unreviewed track
  const handleOpenCategoryReview = useCallback((category: "account" | "intra-album") => {
    const list = category === "account" ? accountMatchTracks : intraAlbumTracks;
    // Start at the first unreviewed track, or fall back to the first track
    const firstUnreviewed = list.find(t => t.reviewAction === null) ?? list[0];
    if (firstUnreviewed != null) {
      handleOpenDialog(firstUnreviewed, category);
    }
  }, [accountMatchTracks, intraAlbumTracks, handleOpenDialog]);

  return (
    <div className="w-full min-w-0" id="albumSelectSongsStep">
      {/* Explainer Section - Only show if there are suspected duplicates or recordings */}
      {hasSuspectedDuplicates && !isImportLoading && (
        <div className={`mb-4 p-4 rounded-lg ${
          isTunecore
            ? "bg-layer-raised border border-stroke-subtle"
            : "bg-layer-raised"
        }`}
        >
          <div className="flex items-start gap-3">
            <div className="flex flex-col flex-1 space-y-4">
              <Text as="subtitle" className="inline-flex gap-2 font-semibold">
                {t("album:track-selection-help") ?? "About Track Selection & Recordings"}
                <IconInfo className={`w-5 h-5 mt-0.5 shrink-0 ${
                  isTunecore ? "stroke-action-primary" : "stroke-action-primary"
                }`}
                />
              </Text>
              {/* Example Button */}
              <Button
                variant={isTunecore ? "secondary" : "primary"}
                label='Review - Title match: "Example Song" (Artist Name)'
                className={`justify-start text-xs font-semibold text-left normal-case whitespace-normal pointer-events-none w-max ${
                  !isTunecore ? "bg-action-secondary hover:bg-action-secondary-hover active:bg-action-secondary-pressed" : ""
                }`}
              />

              <Text as="p">
                {t("album:suspected-duplicate-explainer") ?? "Some of these tracks match songs already in your catalogue. By default, we'll attach them as recordings of the existing song, since recordings normally share the same writer splits. If a track is actually a different composition (e.g. a different writer split), choose \"Register as New Song\" and confirm the correct splits in the pop-up. Please review each flagged track so we know how to handle it."}
              </Text>

              <Button
                type="button"
                variant="link"
                className="h-10 text-sm underline w-max"
                tooltip
                label={t("songs:what-are-recordings") ?? "What are recordings?"}
                tooltipText={t("songs:recordings-info") ?? "Recordings are the master versions of your song. For example, a Radio Edit, Remix, or Live version. You can attach multiple recordings to the same song registration. Upload all versions of your recordings here, and use the 'Alternative Titles' field to clearly label each one."}
              />

              {/* Review progress cards */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Account title matches card */}
                {accountMatchProgress.total > 0 && (
                  <button
                    type="button"
                    onClick={() => handleOpenCategoryReview("account")}
                    className={`flex items-center gap-3 p-3 text-left rounded-lg border transition-colors ${
                      isTunecore
                        ? "bg-layer-inverse border-stroke-subtle hover:border-stroke-primary"
                        : "bg-white border-stroke-subtle hover:border-stroke-primary"
                    }`}
                  >
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full shrink-0 ${
                      accountMatchProgress.isComplete
                        ? "bg-layer-success text-ink-success"
                        : isTunecore
                          ? "bg-layer-raised text-ink-accent"
                          : "bg-action-primary/10 text-ink-accent"
                    }`}
                    >
                      {accountMatchProgress.isComplete
                        ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                            </svg>
                          )
                        : (
                            <Text as="p" className="text-sm font-bold">
                              {accountMatchProgress.total}
                            </Text>
                          )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Text as="p" className="text-sm font-semibold">
                        {t("album:account-title-matches", "Title Matches in Account")}
                      </Text>
                      <Text
                        as="p"
                        className={`text-xs ${
                          accountMatchProgress.isComplete ? "text-ink-success" : "text-ink-secondary"
                        }`}
                      >
                        {accountMatchProgress.isComplete
                          ? t("album:review-complete", "Complete")
                          : `${accountMatchProgress.reviewed} / ${accountMatchProgress.total} ${t("album:reviewed-lowercase", "reviewed")}`}
                      </Text>
                    </div>
                  </button>
                )}

                {/* Intra-album/playlist variants card */}
                {intraAlbumProgress.total > 0 && (
                  <button
                    type="button"
                    onClick={() => handleOpenCategoryReview("intra-album")}
                    className={`flex items-center gap-3 p-3 text-left rounded-lg border transition-colors ${
                      isTunecore
                        ? "bg-layer-inverse border-stroke-subtle hover:border-stroke-primary"
                        : "bg-white border-stroke-subtle hover:border-stroke-primary"
                    }`}
                  >
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full shrink-0 ${
                      intraAlbumProgress.isComplete
                        ? "bg-layer-success text-ink-success"
                        : isTunecore
                          ? "bg-layer-raised text-ink-accent"
                          : "bg-action-primary/10 text-ink-accent"
                    }`}
                    >
                      {intraAlbumProgress.isComplete
                        ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                            </svg>
                          )
                        : (
                            <Text as="p" className="text-sm font-bold">
                              {intraAlbumProgress.total}
                            </Text>
                          )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Text as="p" className="text-sm font-semibold">
                        {sourceType === "playlist"
                          ? t("album:playlist-variants", "Playlist Variants")
                          : t("album:album-variants", "Album Variants")}
                      </Text>
                      <Text
                        as="p"
                        className={`text-xs ${
                          intraAlbumProgress.isComplete ? "text-ink-success" : "text-ink-secondary"
                        }`}
                      >
                        {intraAlbumProgress.isComplete
                          ? t("album:review-complete", "Complete")
                          : `${intraAlbumProgress.reviewed} / ${intraAlbumProgress.total} ${t("album:reviewed-lowercase", "reviewed")}`}
                      </Text>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Table
        enableColumnResizing={false}
        data={tracks}
        columns={columns}
        hasFilters={false}
        noDataMessage={t("album:no-tracks-found") ?? "No tracks found"}
        isRefetchingData={isImportLoading}
        statsBlock={statsBlock}
        paginationComponent={true}
        enableColumnPinning={true}
      />

      {/* Suspected Duplicate Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="inline-flex items-baseline gap-4">
              {selectedTrackForDialog && (typeof selectedTrackForDialog.suspectedAlbumTrackId === "string" && selectedTrackForDialog.suspectedAlbumTrackId.length > 0)
                ? sourceType === "playlist"
                  ? t("album:review-playlist-variant", "Review Playlist Variant")
                  : t("album:review-album-variant", "Review Album Variant")
                : t("album:review-title-match", "Review Title Match")}
              {totalSuspectedCount > 0 && selectedTrackForDialog && (
                <span className="text-sm font-normal text-ink-secondary">
                  (
                  {currentSuspectedIndex + 1}
                  {" / "}
                  {totalSuspectedCount}
                  )
                </span>
              )}

            </DialogTitle>

          </DialogHeader>

          <div className="py-4 space-y-6">
            {/* Side-by-side comparison for Account Match */}
            {selectedTrackForDialog && (typeof selectedTrackForDialog.suspectedWorkTitle === "string" && selectedTrackForDialog.suspectedWorkTitle.length > 0) && !(typeof selectedTrackForDialog.suspectedAlbumTrackId === "string" && selectedTrackForDialog.suspectedAlbumTrackId.length > 0) && (() => {
              const matchedWork = userSongs.find(s => s.workcode === selectedTrackForDialog.suspectedWorkCode);
              const performingArtists = matchedWork?.performingartists
                ?.filter(pa => pa != null && !pa.isdeleted && typeof pa.performingartistname === "string" && pa.performingartistname.length > 0)
                .map(pa => pa.performingartistname)
                .join(", ");

              return (
                <div className="space-y-4">
                  {/* Comparison Cards */}
                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Existing Song Card */}
                    <div className={`p-4 border rounded-lg ${
                      isTunecore
                        ? "bg-layer-raised border-stroke-subtle"
                        : "bg-layer-disabled border-stroke-subtle"
                    }`}
                    >
                      <Text as="p" className="mb-2 text-xs font-semibold uppercase text-ink-secondary">
                        {t("album:existing-song") ?? "Existing Song"}
                      </Text>
                      <Text as="p" className="text-lg font-semibold">
                        {selectedTrackForDialog.suspectedWorkTitle}
                      </Text>
                      {typeof performingArtists === "string" && performingArtists.length > 0 && (
                        <Text as="p" className="text-sm text-ink-secondary">
                          {performingArtists}
                        </Text>
                      )}
                      <Text as="p" className="mt-2 text-xs italic text-ink-secondary">
                        {t("album:already-in-account-label") ?? "Already in account"}
                      </Text>
                    </div>

                    {/* New Recording Card */}
                    <div className={`p-4 border-2 rounded-lg ${
                      isTunecore
                        ? "bg-layer-raised border-stroke-primary"
                        : "border-stroke-primary bg-action-primary/5"
                    }`}
                    >
                      <Text as="p" className="mb-2 text-xs font-semibold uppercase text-ink-secondary">
                        {t("album:new-recording") ?? "New Recording"}
                      </Text>
                      <Text as="p" className="text-lg font-semibold">
                        {selectedTrackForDialog.name}
                      </Text>
                      {selectedTrackForDialog.artists?.[0]?.name && (
                        <Text as="p" className="text-sm text-ink-secondary">
                          {selectedTrackForDialog.artists.map(a => a.name).join(", ")}
                        </Text>
                      )}
                    </div>
                  </div>

                  {/* Explanation */}
                  <Text as="p" className="text-sm text-ink-secondary">
                    {t("album:review-variant-explanation")}
                  </Text>

                  {/* Change target work */}
                  <Combobox
                    smallFont
                    label={t("album:attach-to-different-song") ?? "Attach to a different song:"}
                    data={workOptions}
                    onValueChange={(workCode) => {
                      if (typeof workCode === "string") {
                        setSelectedWorkForDialog(workCode);
                      }
                    }}
                    value={selectedWorkForDialog}
                    placeholder={t("album:select-song-placeholder") ?? "Select song..."}
                    searchPlaceholder={t("album:search-existing-song") ?? "Search for existing song..."}
                    emptyText={t("album:no-songs-found") ?? "No songs found"}
                    searchByLabel
                  />
                </div>
              );
            })()}

            {/* Side-by-side comparison for Intra-Album Match */}
            {selectedTrackForDialog && (typeof selectedTrackForDialog.suspectedAlbumTrackId === "string" && selectedTrackForDialog.suspectedAlbumTrackId.length > 0) && (() => {
              const originalTrack = tracks.find(t => t.id === selectedTrackForDialog.suspectedAlbumTrackId);

              return (
                <div className="space-y-4">
                  {/* Comparison Cards */}
                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Original Track Card */}
                    <div className={`p-4 border rounded-lg ${
                      isTunecore
                        ? "bg-layer-raised border-stroke-subtle"
                        : "bg-layer-disabled border-stroke-subtle"
                    }`}
                    >
                      <Text as="p" className="mb-2 text-xs font-semibold uppercase text-ink-secondary">
                        {t("album:original-track") ?? "Original Track"}
                      </Text>
                      <Text as="p" className="text-lg font-semibold">
                        {selectedTrackForDialog.suspectedAlbumTrackTitle}
                      </Text>
                      {typeof originalTrack?.artists?.[0]?.name === "string" && originalTrack.artists[0].name.length > 0 && (
                        <Text as="p" className="text-sm text-ink-secondary">
                          {originalTrack.artists.map(a => a.name).join(", ")}
                        </Text>
                      )}
                      <Text as="p" className="mt-2 text-xs italic text-ink-secondary">
                        {sourceType === "playlist"
                          ? t("album:on-this-playlist", "On this playlist")
                          : t("album:on-this-album", "On this album")}
                      </Text>
                    </div>

                    {/* Variant Recording Card */}
                    <div className={`p-4 border-2 rounded-lg ${
                      isTunecore
                        ? "bg-layer-raised border-stroke-primary"
                        : "border-stroke-primary bg-action-primary/5"
                    }`}
                    >
                      <Text as="p" className="mb-2 text-xs font-semibold uppercase text-ink-secondary">
                        {t("album:variant-recording") ?? "Recording"}
                      </Text>
                      <Text as="p" className="text-lg font-semibold">
                        {selectedTrackForDialog.name}
                      </Text>
                      {selectedTrackForDialog.artists?.[0]?.name && (
                        <Text as="p" className="text-sm text-ink-secondary">
                          {selectedTrackForDialog.artists.map(a => a.name).join(", ")}
                        </Text>
                      )}
                    </div>
                  </div>

                  {/* Explanation */}
                  <Text as="p" className="text-sm text-ink-secondary">
                    {t("album:review-variant-explanation")}
                  </Text>
                </div>
              );
            })()}

            {/* Create as New Song Section - Collapsed by default */}
            <div className={`p-4 border rounded-lg ${
              isTunecore
                ? "bg-layer-raised border-stroke-subtle"
                : "border-stroke-subtle"
            }`}
            >
              <Text as="p" className="mb-1 text-sm font-semibold">
                {t("album:different-writers") ?? "Different writers?"}
              </Text>

              {selectedAuxiliaryWriters.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedAuxiliaryWriters.map((writerCode) => {
                    const writer = userWriters.find(w => w.writercode === writerCode);
                    if (!writer)
                      return null;
                    const displayName = writer.writername ?? `${writer.firstname ?? ""} ${writer.lastname ?? ""}`.trim();
                    return (
                      <div
                        key={writerCode}
                        className={`inline-flex items-center gap-2 px-3 py-1 text-sm rounded-full ${
                          isTunecore
                            ? "bg-layer-inverse"
                            : "bg-layer-disabled"
                        }`}
                      >
                        <Text as="p" className="text-xs">{displayName}</Text>
                        <button
                          type="button"
                          onClick={() => setSelectedAuxiliaryWriters(prev => prev.filter(w => w !== writerCode))}
                          className="text-ink-secondary hover:text-ink-primary"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <Combobox
                smallFont
                label={t("album:different-writers-description") ?? "Add the writers who worked on this version to create it as a separate song with its own splits."}
                data={userWriters
                  .filter(writer => !selectedAuxiliaryWriters.includes(writer.writercode))
                  .map((writer) => {
                    const displayName = writer.writername ?? `${writer.firstname ?? ""} ${writer.lastname ?? ""}`.trim();
                    const caeIpiText = (writer.caeipinumber !== null && writer.caeipinumber !== undefined && writer.caeipinumber !== "") ? ` (CAE/IPI: ${writer.caeipinumber})` : "";
                    return {
                      label: `${displayName}${caeIpiText}`,
                      value: writer.writercode,
                    };
                  })}
                onValueChange={(writerCode) => {
                  if (typeof writerCode === "string" && writerCode.length > 0) {
                    setSelectedAuxiliaryWriters((prev) => {
                      if (prev.includes(writerCode)) {
                        return prev.filter(w => w !== writerCode);
                      }
                      return [...prev, writerCode];
                    });
                  }
                }}
                value=""
                placeholder={t("album:select-writers-placeholder") ?? "Select additional writers..."}
                searchPlaceholder={t("album:search-writers") ?? "Search writers..."}
                emptyText={t("album:no-writers-found2") ?? "No writers found"}
                searchByLabel
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse justify-between gap-2 mt-2 sm:flex-row">
            <div className="flex items-center gap-2">
              <Button
                variant={isTunecore ? "primary" : "secondary"}
                label={t("album:previous") ?? "Previous"}
                onClick={handlePreviousSuspected}
                disabled={!hasPreviousSuspected}
                className="w-min"
              />
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                variant="link"
                label={t("album:dismiss", "Dismiss")}
                onClick={handleDismissFalsePositive}
                className="w-min"

              />
              <Button
                variant={isTunecore ? "secondary" : "primary"}
                label={t("album:create-as-new-song") ?? "Create as New Song"}
                onClick={handleMarkAsNewWork}
                disabled={selectedAuxiliaryWriters.length === 0}
                className="w-full sm:w-auto"
              />
              {selectedAuxiliaryWriters.length === 0 && (
                <Button
                  variant={isTunecore ? "secondary" : "primary"}
                  label={t("album:attach-as-recording") ?? "Attach as Recording"}
                  onClick={handleConfirmAsRecording}
                  disabled={!(selectedTrackForDialog && (typeof selectedTrackForDialog.suspectedAlbumTrackId === "string" && selectedTrackForDialog.suspectedAlbumTrackId.length > 0)) && !selectedWorkForDialog}
                  className="justify-center w-full whitespace-normal sm:w-auto"
                />
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Link to Existing Song Modal */}
      <LinkToExistingSongModal
        open={linkModalOpen}
        onOpenChange={setLinkModalOpen}
        track={selectedTrackForLink ? {
          id: selectedTrackForLink.id,
          name: selectedTrackForLink.name,
          artistName: selectedTrackForLink.artists?.map(a => a.name).join(", "),
          isrc: selectedTrackForLink.external_ids?.isrc,
        } : null}
        accountSongOptions={workOptions}
        albumTrackOptions={albumTrackOptions}
        onConfirmAccountLink={handleConfirmAccountLink}
        onConfirmAlbumLink={handleConfirmAlbumLink}
        isLoading={isImportLoading || songsLoading}
      />
    </div>
  );
}
